/* eslint-disable react/jsx-props-no-spreading */
import { useUser } from '@folklore/auth';
import { useRoutes, useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect, Route, Switch, useLocation } from 'wouter';

import { useAuthLogout } from '../../hooks/useAuth';
import useScrollEnter from '../../hooks/useScrollEnter';
import { usePageViewTracking } from '../../hooks/useTracking';

import { useIsCheckingAuth } from '../../contexts/AuthContext';
import { useDefaultBrand } from '../../contexts/SiteContext';
import BrandLogoButton from '../buttons/BrandLogoButton';
import RoundedButton from '../buttons/RoundedButton';
import AccountMenu from '../menus/AccountMenu';
import AccountPanel from '../panels/AccountPanel';
import AccountProfilePanel from '../panels/AccountProfilePanel';
import AccountSubscriptionsPanel from '../panels/AccountSubscriptionsPanel';
import AccountTimelinePanel from '../panels/AccountTimelinePanel';

import styles from '../../styles/pages/account-page.module.css';

interface AccountPageProps {
    current?: boolean;
    entered?: boolean;
    entering?: boolean;
    leaving?: boolean;
    className?: string | null;
    contentClassName?: string | null;
    onEnter?: () => void;
    onLeave?: () => void;
}

function AccountPage({
    entered = false,
    entering = false,
    leaving = false,
    current = false,
    className = null,
    contentClassName = null,
    onEnter = null,
    onLeave = null,
}: AccountPageProps): JSX.Element | null {
    const defaultBrand = useDefaultBrand();

    // useScrollEnter({
    //     disabled: !current,
    //     onEnter: onEnter || undefined,
    //     onLeave: onLeave || undefined,
    // });

    const routes = useRoutes();
    const [location] = useLocation();
    const url = useUrlGenerator();
    const user = useUser();

    const isCheckingAuth = useIsCheckingAuth();

    usePageViewTracking(current && user !== null && !isCheckingAuth, {
        pageType: 'account',
    });

    const { logout } = useAuthLogout();
    const onClickLogout = useCallback(() => {
        logout();
    }, [logout]);

    if (user === null && isCheckingAuth) {
        return null;
    }

    if (user === null && current) {
        return <Redirect to={`${url('login')}?next=${encodeURIComponent(location)}`} replace />;
    }

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.entered]: entered,
                    [styles.entering]: entering,
                    [styles.leaving]: leaving,
                },
                className,
                contentClassName,
            ])}
            data-brand="urbania"
            data-theme="dark"
        >
            <BrandLogoButton brand={defaultBrand} className={styles.urbania} />
            {/* <UrbaniaButton className={styles.urbania} /> */}
            <div className={styles.inner}>
                <AccountMenu className={styles.menu} />
                <Switch>
                    <Route path={routes['account.timeline']}>
                        <AccountTimelinePanel className={styles.panel} />
                    </Route>
                    <Route path={routes['account.subscriptions']}>
                        <AccountSubscriptionsPanel className={styles.panel} />
                    </Route>
                    <Route path={routes['account.profile']}>
                        <AccountProfilePanel className={styles.panel} />
                    </Route>
                    <Route>
                        <AccountPanel className={styles.panel} />
                    </Route>
                </Switch>
                <div className={styles.footer}>
                    <RoundedButton transparent className={styles.logout} onClick={onClickLogout}>
                        <FormattedMessage
                            defaultMessage="Se déconnecter"
                            description="Button Label"
                        />
                    </RoundedButton>
                </div>
            </div>
        </div>
    );
}

export default AccountPage;
